import { ApexOptions } from 'apexcharts';
import dayjs from 'dayjs';
import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { ColorRing } from 'react-loader-spinner';
import { useParams, useSearchParams } from 'react-router-dom';

import { useCapacityAnalytics } from 'hooks/Traps/CapacityAnalytics/useCapacityAnalytics';
import {
  CapacityAnalyticsParams,
  CapacityAnalyticsSeries,
  CapacityEventType,
  CapacitySeriesKey,
} from 'api/traps/CapacityAnalytics/types';

const EVENT_TYPE_OPTIONS: Array<{ label: string; value: CapacityEventType | 'all' }> = [
  { label: 'All events', value: 'all' },
  { label: 'Monitoring', value: 'monitoring' },
  { label: 'Trigger', value: 'trigger' },
];

const SERIES_OPTIONS: Array<{ label: string; value: CapacitySeriesKey }> = [
  { label: 'Capacity 1', value: 'capacity11' },
  { label: 'Capacity 2', value: 'capacity21' },
];

const toDateTimeLocalValue = (date: dayjs.Dayjs) => date.format('YYYY-MM-DDTHH:mm');

const initialDateFrom = () => toDateTimeLocalValue(dayjs().subtract(24, 'hour'));
const initialDateTo = () => toDateTimeLocalValue(dayjs());

const toApiDateTime = (value: string) => dayjs(value).toISOString();

interface ApexPoint {
  x: string | number;
  y: number | null;
}

const withGapBreaks = (series: CapacityAnalyticsSeries, maxGapSeconds: number): ApexPoint[] => {
  const points: ApexPoint[] = [];
  const maxGapMs = maxGapSeconds * 1000;

  series.data.forEach(point => {
    const currentTime = new Date(point.x).getTime();
    const previous = points[points.length - 1];
    const previousTime = previous ? new Date(previous.x).getTime() : null;

    if (previousTime !== null && currentTime - previousTime > maxGapMs) {
      points.push({ x: previousTime + 1, y: null });
    }

    points.push({ x: point.x, y: point.y });
  });

  return points;
};

const CapacityAnalyticsPage = () => {
  const { slug = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [dateFrom, setDateFrom] = useState(searchParams.get('date_from') || initialDateFrom());
  const [dateTo, setDateTo] = useState(searchParams.get('date_to') || initialDateTo());
  const [eventType, setEventType] = useState<CapacityEventType | 'all'>(
    (searchParams.get('event_type') as CapacityEventType | 'all' | null) || 'monitoring',
  );
  const [visibleSeries, setVisibleSeries] = useState<Record<CapacitySeriesKey, boolean>>({
    capacity11: searchParams.get('series') !== 'capacity21',
    capacity21: searchParams.get('series') !== 'capacity11',
  });

  const { data, error, isLoading, load } = useCapacityAnalytics(slug);

  const requestParams = useMemo<CapacityAnalyticsParams>(
    () => ({
      date_from: toApiDateTime(dateFrom),
      date_to: toApiDateTime(dateTo),
      event_type: eventType,
    }),
    [dateFrom, dateTo, eventType],
  );
  const [appliedParams, setAppliedParams] = useState<CapacityAnalyticsParams>(requestParams);

  const updateUrl = () => {
    const params = new URLSearchParams();
    params.set('date_from', dateFrom);
    params.set('date_to', dateTo);
    params.set('event_type', eventType);

    const activeSeries = SERIES_OPTIONS.filter(series => visibleSeries[series.value]).map(series => series.value);
    params.set('series', activeSeries.length === 1 ? activeSeries[0] : 'both');
    setSearchParams(params);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateUrl();
    setAppliedParams(requestParams);
  };

  const handleSeriesChange = (seriesKey: CapacitySeriesKey) => {
    setVisibleSeries(current => {
      const next = { ...current, [seriesKey]: !current[seriesKey] };
      if (!next.capacity11 && !next.capacity21) {
        return current;
      }
      return next;
    });
  };

  useEffect(() => {
    load(appliedParams);
  }, [load, appliedParams]);

  const chartSeries = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.series
      .filter(series => visibleSeries[series.key])
      .map(series => ({
        name: series.name,
        data: withGapBreaks(series, data.meta.max_adaptive_interval_seconds),
      }));
  }, [data, visibleSeries]);

  const deltaChartSeries = useMemo(() => {
    if (!data) {
      return [];
    }

    return data.delta_series
      .filter(series => visibleSeries[series.key])
      .map(series => ({
        name: series.name,
        data: withGapBreaks(series, data.meta.max_adaptive_interval_seconds),
      }));
  }, [data, visibleSeries]);

  const chartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        id: 'capacity-analytics-chart',
        type: 'line',
        zoom: { enabled: true },
        toolbar: { show: true },
      },
      colors: ['#405189', '#0ab39c'],
      dataLabels: { enabled: false },
      markers: { size: 3 },
      stroke: {
        curve: 'straight',
        width: 2,
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: { text: 'Capacity' },
      },
      tooltip: {
        x: {
          format: 'dd MMM yyyy HH:mm',
        },
      },
      noData: {
        text: 'No capacity data for selected filters',
      },
      legend: {
        position: 'top',
      },
    }),
    [],
  );

  const deltaChartOptions = useMemo<ApexOptions>(
    () => ({
      chart: {
        id: 'capacity-delta-chart',
        type: 'bar',
        zoom: { enabled: true },
        toolbar: { show: true },
      },
      colors: ['#405189', '#0ab39c'],
      dataLabels: { enabled: false },
      plotOptions: {
        bar: {
          columnWidth: '70%',
        },
      },
      xaxis: {
        type: 'datetime',
        labels: {
          datetimeUTC: false,
        },
      },
      yaxis: {
        title: { text: 'Delta' },
      },
      tooltip: {
        x: {
          format: 'dd MMM yyyy HH:mm',
        },
      },
      noData: {
        text: 'No capacity delta data for selected filters',
      },
      legend: {
        position: 'top',
      },
    }),
    [],
  );

  const isEmpty = !isLoading && !error && data !== null && data.points_count === 0;

  return (
    <div className="page-content bg-light">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <div>
            <h4 className="mb-1">Capacity analytics</h4>
            <div className="text-muted">
              {data?.trap.serial_number || slug}
              {data?.trap.sequence_number ? ` · #${data.trap.sequence_number}` : ''}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <form className="row g-3 align-items-end" onSubmit={handleSubmit}>
              <div className="col-md-3">
                <label className="form-label" htmlFor="capacity-date-from">
                  Date from
                </label>
                <input
                  id="capacity-date-from"
                  className="form-control"
                  type="datetime-local"
                  value={dateFrom}
                  onChange={event => setDateFrom(event.target.value)}
                  required
                />
              </div>
              <div className="col-md-3">
                <label className="form-label" htmlFor="capacity-date-to">
                  Date to
                </label>
                <input
                  id="capacity-date-to"
                  className="form-control"
                  type="datetime-local"
                  value={dateTo}
                  onChange={event => setDateTo(event.target.value)}
                  required
                />
              </div>
              <div className="col-md-2">
                <label className="form-label" htmlFor="capacity-event-type">
                  Event type
                </label>
                <select
                  id="capacity-event-type"
                  className="form-select"
                  value={eventType}
                  onChange={event => setEventType(event.target.value as CapacityEventType | 'all')}
                >
                  {EVENT_TYPE_OPTIONS.map(option => (
                    <option value={option.value} key={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-3">
                <span className="form-label d-block">Series</span>
                <div className="d-flex gap-3">
                  {SERIES_OPTIONS.map(series => (
                    <div className="form-check" key={series.value}>
                      <input
                        id={`capacity-series-${series.value}`}
                        className="form-check-input"
                        type="checkbox"
                        checked={visibleSeries[series.value]}
                        onChange={() => handleSeriesChange(series.value)}
                      />
                      <label className="form-check-label" htmlFor={`capacity-series-${series.value}`}>
                        {series.label}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="col-md-1">
                <button className="btn btn-primary w-100" type="submit" disabled={isLoading}>
                  Apply
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="card">
          <div className="card-body" style={{ minHeight: 460 }}>
            {isLoading && (
              <div
                className="d-flex align-items-center justify-content-center"
                style={{ minHeight: 390 }}
                data-testid="capacity-loading"
              >
                <ColorRing
                  visible
                  height="80"
                  width="80"
                  ariaLabel="color-ring-loading"
                  wrapperClass="color-ring-wrapper"
                  colors={['#405189', '#0AB39C', '#F7B84B', '#F06548', '#299CDB']}
                />
              </div>
            )}
            {!isLoading && error && (
              <div className="alert alert-danger mb-0" role="alert">
                {error}
              </div>
            )}
            {isEmpty && (
              <div className="alert alert-info mb-0" role="status">
                No capacity data for selected period.
              </div>
            )}
            {!isLoading && !error && data && data.points_count > 0 && (
              <>
                <div className="d-flex gap-3 text-muted small mb-2">
                  <span>Records: {data.records_count}</span>
                  <span>Points: {data.points_count}</span>
                  <span>Limit: {data.meta.max_records} records</span>
                </div>
                <ReactApexChart options={chartOptions} series={chartSeries} type="line" height={390} />
              </>
            )}
          </div>
        </div>

        {!isLoading && !error && data && data.points_count > 0 && (
          <div className="card">
            <div className="card-body" style={{ minHeight: 420 }}>
              <div className="d-flex gap-3 text-muted small mb-2">
                <span>Delta points: {data.delta_points_count}</span>
              </div>
              <ReactApexChart options={deltaChartOptions} series={deltaChartSeries} type="bar" height={350} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CapacityAnalyticsPage;
